import {
  RELIGION_CLASSIFICATIONS,
  RELIGION_DECLINED,
  DEFAULT_RELIGION_CLASSIFICATION,
  DEFAULT_RELIGION_LEVEL
} from 'src/components/questionnaire/Constants';

const byDisplay = (a, b) => a.display.localeCompare(b.display);

const overrideKey = (entry) => `${entry?.system || ''}|${entry?.code || ''}`;

export const religionClassificationOptions = () =>
  RELIGION_CLASSIFICATIONS.map(
    ({ key, label, publisher, sourceTitle, sourceUrl, version, levels }) => ({
      key,
      label,
      publisher,
      sourceTitle,
      sourceUrl,
      version,
      levels: levels.map((l) => ({
        key: l.key,
        label: l.label,
        system: l.system,
        count: l.concepts.length
      }))
    })
  );

export const findReligionClassification = (key) =>
  RELIGION_CLASSIFICATIONS.find((c) => c.key === key) ||
  RELIGION_CLASSIFICATIONS.find(
    (c) => c.key === DEFAULT_RELIGION_CLASSIFICATION
  );

export const findReligionLevel = (classificationKey, levelKey) => {
  const classification = findReligionClassification(classificationKey);
  const level =
    classification.levels.find((l) => l.key === levelKey) ||
    classification.levels.find((l) => l.key === DEFAULT_RELIGION_LEVEL) ||
    classification.levels[0];
  return { classification, level };
};

export function readReligionSettings(responseData) {
  if (Array.isArray(responseData)) {
    return {
      classification: DEFAULT_RELIGION_CLASSIFICATION,
      level: DEFAULT_RELIGION_LEVEL,
      religion: responseData
    };
  }
  return {
    classification:
      responseData?.religionClassification ||
      responseData?.classification ||
      DEFAULT_RELIGION_CLASSIFICATION,
    level:
      responseData?.religionLevel ||
      responseData?.level ||
      DEFAULT_RELIGION_LEVEL,
    religion: Array.isArray(responseData?.religion) ? responseData.religion : []
  };
}

export function mergeReligionRows(settings) {
  const { classification, level, religion } = readReligionSettings(settings);
  const resolved = findReligionLevel(classification, level);

  const overrides = new Map(
    (Array.isArray(religion) ? religion : []).map((e) => [overrideKey(e), e])
  );

  const rows = resolved.level.concepts.map((concept) => {
    const row = {
      system: resolved.level.system,
      code: concept.code,
      display: concept.display,
      group: concept.group || '',
      show: concept.defaultShow ?? true,
      topOfList: false
    };
    const override = overrides.get(overrideKey(row));
    if (override) {
      if (typeof override.show === 'boolean') row.show = override.show;
      if (typeof override.topOfList === 'boolean') {
        row.topOfList = override.topOfList;
      }
    }
    return row;
  });

  const declined = {
    ...RELIGION_DECLINED,
    group: '',
    show: true,
    topOfList: false
  };
  const declinedOverride = overrides.get(overrideKey(RELIGION_DECLINED));
  if (declinedOverride) {
    if (typeof declinedOverride.show === 'boolean') {
      declined.show = declinedOverride.show;
    }
    if (typeof declinedOverride.topOfList === 'boolean') {
      declined.topOfList = declinedOverride.topOfList;
    }
  }

  return [...rows, declined];
}

export function resolveReligionOptions(settings) {
  return mergeReligionRows(settings)
    .filter((r) => r.show)
    .sort((a, b) => {
      if (a.topOfList !== b.topOfList) return a.topOfList ? -1 : 1;
      return byDisplay(a, b);
    })
    .map(({ system, code, display }) => ({ system, code, display }));
}

export const isSameReligion = (a, b) =>
  !!a && !!b && a.system === b.system && a.code === b.code;

export const normaliseReligion = (value) =>
  value && value.code
    ? {
        system: value.system || '',
        code: value.code,
        display: value.display || ''
      }
    : null;

export function religionLabel(value, settings) {
  if (!value || !value.code) return '';
  if (settings) {
    const match = mergeReligionRows(settings).find((r) =>
      isSameReligion(r, value)
    );
    if (match) return match.display;
  }
  return value.display || value.code;
}

export function deriveReligionBulkState(inScope) {
  const rows = Array.isArray(inScope) ? inScope : [];
  const total = rows.length;
  const shown = rows.filter((e) => e.show).length;
  const hidden = total - shown;
  const pinned = rows.filter((e) => e.topOfList).length;

  return {
    total,
    shown,
    hidden,
    pinned,
    visibility:
      total > 0 && hidden === 0
        ? { action: 'hide', patch: { show: false }, count: total }
        : { action: 'show', patch: { show: true }, count: hidden },
    pin:
      pinned === 0
        ? { action: 'pin', patch: { topOfList: true }, count: total }
        : { action: 'unpin', patch: { topOfList: false }, count: pinned }
  };
}

export {
  RELIGION_CLASSIFICATIONS,
  RELIGION_DECLINED,
  DEFAULT_RELIGION_CLASSIFICATION,
  DEFAULT_RELIGION_LEVEL
};
